/**
 * IhsPrimitives — IHS-domain SVG decorations.
 *
 * After Phase 0b extraction (`docs/PRIMITIVES-AUDIT.md`) the generic
 * shared atoms (CircularGauge, MiniStat, CountryFlag, JerseySlot) moved
 * to `components/primitives/`. What remains here is domain art that
 * is specific to football match-detail widgets and not promoted to a
 * cross-surface primitive:
 *
 *   - `<GoalNetSvg>` — stylised goal-net divider with optional inline
 *     on-target value circles. Used by IhsGoals + IhsShots only.
 */

import type { ReactElement } from 'react';
import styles from './IhsPrimitives.module.css';

/* ─── GoalNetSvg ─────────────────────────────────────────────────── */

interface GoalNetSvgProps {
  width?: number;
  /**
   * Optional home on-target value rendered as a filled blue circle
   * INSIDE the net, on the left. Used by IhsShots so the on-target
   * counts live visually inside the goal-net itself.
   */
  homeOnTarget?: string | null;
  awayOnTarget?: string | null;
  /** Colour for the home on-target circle. Defaults to the brand `--team-home` token. */
  homeColor?: string;
  /** Colour for the away on-target circle. Defaults to the brand `--team-away` token. */
  awayColor?: string;
}

/**
 * Small stylised goal-net SVG used as a visual divider between home and
 * away stat values. Greyscale + a tiny soccer ball in the upper-right.
 *
 * When `homeOnTarget` / `awayOnTarget` are supplied, the SVG also renders
 * two filled value circles INSIDE the net — left for home, right for
 * away — used by the IhsShots composite row. These render-inside circles
 * are positioned in SVG user space, not via DOM overlays, so they scale
 * cleanly with `width`.
 */
export function GoalNetSvg({
  width = 80,
  homeOnTarget,
  awayOnTarget,
  homeColor = 'var(--team-home, #1976D2)',
  awayColor = 'var(--team-away, #F47B1F)',
}: GoalNetSvgProps): ReactElement {
  const height = Math.round(width * 0.75);
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 80 60"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.goalNet}
      aria-hidden="true"
    >
      {/* Goal frame */}
      <rect
        x="10"
        y="8"
        width="60"
        height="40"
        fill="#ffffff"
        stroke="#1A1A1A"
        strokeWidth="1.5"
      />
      {/* Net grid — verticals */}
      <line x1="20" y1="8" x2="20" y2="48" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="30" y1="8" x2="30" y2="48" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="40" y1="8" x2="40" y2="48" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="50" y1="8" x2="50" y2="48" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="60" y1="8" x2="60" y2="48" stroke="#D8D5D0" strokeWidth="0.8" />
      {/* Net grid — horizontals */}
      <line x1="10" y1="18" x2="70" y2="18" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="10" y1="28" x2="70" y2="28" stroke="#D8D5D0" strokeWidth="0.8" />
      <line x1="10" y1="38" x2="70" y2="38" stroke="#D8D5D0" strokeWidth="0.8" />
      {/* Ground line */}
      <line x1="2" y1="49" x2="78" y2="49" stroke="#1A1A1A" strokeWidth="1" />
      {/* Soccer ball — upper right */}
      <circle cx="62" cy="14" r="4" fill="#ffffff" stroke="#1A1A1A" strokeWidth="0.8" />
      <path
        d="M62 10.5 L60.5 13 L62 14.5 L63.5 13 Z"
        fill="#1A1A1A"
      />
      {/* On-target value circles — only when both supplied.
          Paint goes through `style`, not `fill=`: the team colours are
          `var()` strings since ADR-063, and SVG presentation attributes
          do not resolve `var()`. */}
      {homeOnTarget !== undefined && homeOnTarget !== null ? (
        <>
          <circle cx="24" cy="30" r="10" style={{ fill: homeColor }} />
          <text
            x="24"
            y="30"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="Inter, sans-serif"
            fontSize="9"
            fontWeight="700"
            style={{ fill: 'var(--text-inverse, #FFFFFF)' }}
          >
            {homeOnTarget}
          </text>
        </>
      ) : null}
      {awayOnTarget !== undefined && awayOnTarget !== null ? (
        <>
          <circle cx="56" cy="30" r="10" style={{ fill: awayColor }} />
          <text
            x="56"
            y="30"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="Inter, sans-serif"
            fontSize="9"
            fontWeight="700"
            style={{ fill: 'var(--text-inverse, #FFFFFF)' }}
          >
            {awayOnTarget}
          </text>
        </>
      ) : null}
    </svg>
  );
}
